import { Dispatch, PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  GetVaccinesParams,
  PaginatedResponse,
  Pagination,
  Vaccine,
  VaccinePetsListItem,
  typicodeApi,
} from 'shared/api';

type TVaccinePetsState = {
  list: Vaccine[];
  vaccineList: VaccinePetsListItem[];
  pagination: Pagination | null;
};

const initialState: TVaccinePetsState = {
  list: [],
  vaccineList: [],
  pagination: null,
};

export const vaccinePetsModel = createSlice({
  name: 'vaccinesPets',
  initialState,
  reducers: {
    setVaccines: (state, { payload }: PayloadAction<Vaccine[]>) => {
      state.list = payload;
    },
    setVaccineList: (state, { payload }: PayloadAction<VaccinePetsListItem[]>) => {
      state.vaccineList = payload;
    },
    setPagination: (state, { payload }: PayloadAction<Pagination>) => {
      state.pagination = payload;
    },
    addVaccines: (state, { payload }: PayloadAction<Vaccine>) => {
      state.list = [...state.list, payload];
    },
    updateDiseases: (state, { payload }: PayloadAction<Vaccine>) => {
      state.list = state.list.map((item) => (item._id === payload._id ? payload : item));
    },
  },
});

// react-query actions (everything that async)
export const VACCINES_PETS_ALL_QUERY_KEY = 'vaccines-pets-all';
const VACCINE_PETS_BY_ID_QUERY_KEY = 'vaccine-pets-by-id';

export const getVaccinesPetsAllAsync = (params: GetVaccinesParams) => (dispatch: Dispatch) =>
  useQuery<AxiosResponse<PaginatedResponse<VaccinePetsListItem>>>(
    [VACCINES_PETS_ALL_QUERY_KEY, params],
    () => typicodeApi.vaccinePets.getVaccinesAll(params),
    {
      onSuccess: ({ data }) => {
        dispatch(vaccinePetsModel.actions.setVaccineList(data.data));
        dispatch(vaccinePetsModel.actions.setPagination(data.pagination));
      },
      onError: () => {
        toast.error('There was an error loading the list of vaccines, please try reloading the page!');
      },
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

export const getVaccinePetsByIdAsync = (id: string, enabled: boolean = true) =>
  useQuery<AxiosResponse<Vaccine>>(
    [VACCINE_PETS_BY_ID_QUERY_KEY, id],
    () => typicodeApi.vaccinePets.getVaccineById(id),
    {
      enabled,
      staleTime: 0, // Данные сразу считаются устаревшими - всегда свежие при открытии
      cacheTime: 0, // Не кешировать - каждый раз загружать заново
      refetchOnWindowFocus: false,
    },
  );

export const postNewVaccinePetsAsync = () => {
  const queryClient = useQueryClient();
  return useMutation(typicodeApi.vaccinePets.postVaccine, {
    onSuccess: () => {
      toast.success('The vaccine has been successfully added!');
      void queryClient.invalidateQueries(VACCINES_PETS_ALL_QUERY_KEY);
    },
    onError: () => {
      toast.error('Error when adding a new vaccine. Try again!');
    },
  });
};

export const updateVaccinePetsAsync = () => {
  const queryClient = useQueryClient();
  return useMutation(typicodeApi.vaccinePets.patchVaccine, {
    onSuccess: () => {
      toast.success('The vaccine has been successfully modified!');
      void queryClient.invalidateQueries(VACCINES_PETS_ALL_QUERY_KEY);
    },
    onError: () => {
      toast.error('Error when changing the vaccine. Try again!');
    },
  });
};

// selectors
export const useVaccinesPets = () => useSelector((state: RootState) => state.vaccinesPets);
export const useVaccinePetsList = () =>
  useSelector((state: RootState) => ({
    vaccineList: state.vaccinesPets.vaccineList,
    pagination: state.vaccinesPets.pagination,
  }));
export const isVaccinesPetsEmpty = (): boolean =>
  useSelector(
    createSelector(
      (state: RootState) => state.vaccinesPets.list,
      (vaccines) => vaccines.length === 0,
    ),
  );
export const isVaccinePetsListEmpty = (): boolean =>
  useSelector(
    createSelector(
      (state: RootState) => state.vaccinesPets.vaccineList,
      (vaccines) => vaccines.length === 0,
    ),
  );

export const reducerPets = vaccinePetsModel.reducer;
